import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Row,
  Col,
} from 'react-bootstrap';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaHome,
  FaChartLine,
  FaEye,
  FaEyeSlash,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Header from '../../components/common/Header';
import TestService from '../../services/testService';
import '../../styles/test-result.css';

const TestResult = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  
  const [result, setResult] = useState(null);
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadResult();
  }, [attemptId]);

  const loadResult = async () => {
    try {
      setLoading(true);
      const response = await TestService.getResult(attemptId);
      console.log('Result data:', response.data);
      
      const resultData = response.data;
      setResult(resultData);
      
      // Set test details (passing percentage) directly from result API
      if (resultData && resultData.passing_percentage) {
        setTest({ passing_percentage: resultData.passing_percentage });
      }
    } catch (error) {
      console.error('Failed to load result:', error);
      toast.error('Failed to load result');
      navigate('/student/tests');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const d = new Date(date);
    return d.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading result...</p>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div>
        <Header />
        <Container className="py-5 text-center">
          <Alert variant="danger">Result not found</Alert>
          <Button variant="primary" onClick={() => navigate('/student/tests')}>
            Back to Tests
          </Button>
        </Container>
      </div>
    );
  }

  const percentage = parseFloat(result.percentage) || 0;
  const passed = result.passed === 1 || result.passed === true;
  const score = parseInt(result.score) || 0;
  const total = parseInt(result.total_possible) || 0;
  
  // FIX 1: Get passing percentage from test data or result
  const passingPercentage = test?.passing_percentage || result.passing_percentage || 60;
  
  // Get answers and questions
  const answers = result.answers || {};
  const questions = result.selected_questions || [];

  // Calculate correct/incorrect counts
  const correctCount = Object.values(answers).filter(a => a?.is_correct === true).length;
  const incorrectCount = Object.values(answers).filter(a => a && !a.is_correct).length;

  return (
    <div>
      <Header />
      <div className="test-result-page">
        <Container className="py-4">
          <Button
            variant="link"
            className="text-decoration-none mb-3"
            onClick={() => navigate('/student/tests')}
          >
            ← Back to Tests
          </Button>

          {/* Result Card */}
          <Card className={`result-card mb-4 ${passed ? 'passed' : 'failed'}`}>
            <Card.Body className="text-center py-5">
              <div className="result-icon">
                {passed ? <FaCheckCircle /> : <FaTimesCircle />}
              </div>
              <h2 className="result-title">{result.test_title || 'Test'}</h2>
              <div className="result-score">
                <span className="score-number">{score}</span>
                <span className="score-total">/{total}</span>
              </div>
              <div className="result-percentage">
                <span className={`percentage ${passed ? 'text-success' : 'text-danger'}`}>
                  {percentage.toFixed(2)}%
                </span>
                <Badge bg={passed ? 'success' : 'danger'} className="ms-2">
                  {passed ? 'PASSED' : 'FAILED'}
                </Badge>
              </div>
              <div className="result-message mt-3">
                {passed
                  ? '🎉 Congratulations! You have successfully passed the test.'
                  : '📚 Keep learning! Review the questions below and try again.'}
              </div>
            </Card.Body>
          </Card>

          {/* Test Details */}
          <Row className="mb-4">
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h6 className="mb-3">📋 Test Information</h6>
                  <div className="info-row">
                    <span className="info-label">Test Name:</span>
                    <span className="info-value">{result.test_title}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Started At:</span>
                    <span className="info-value">{formatDateTime(result.started_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Completed At:</span>
                    <span className="info-value">{formatDateTime(result.completed_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Passing Percentage:</span>
                    <span className="info-value">
                      <strong>{passingPercentage}%</strong>
                      {percentage >= passingPercentage ? (
                        <Badge bg="success" className="ms-2">Achieved</Badge>
                      ) : (
                        <Badge bg="danger" className="ms-2">Need {Math.ceil(passingPercentage - percentage)}% more</Badge>
                      )}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm h-100">
                <Card.Body>
                  <h6 className="mb-3">📊 Performance Summary</h6>
                  <div className="info-row">
                    <span className="info-label">Total Questions:</span>
                    <span className="info-value">{questions.length}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Correct Answers:</span>
                    <span className="info-value text-success">
                      {correctCount}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Incorrect Answers:</span>
                    <span className="info-value text-danger">
                      {incorrectCount}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Total Score:</span>
                    <span className="info-value">{score}/{total}</span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Question-wise Breakdown */}
          <Card className="shadow-sm mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">📝 Question-wise Breakdown</h6>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? <FaEyeSlash className="me-1" /> : <FaEye className="me-1" />}
                  {showDetails ? 'Hide Details' : 'Show Details'}
                </Button>
              </div>
              
              {showDetails && questions.length > 0 && (
                <div className="question-breakdown-list">
                  {questions.map((question, idx) => {
                    const answer = answers[question.id];
                    const isCorrect = answer?.is_correct;
                    const userAnswer = answer?.selected;
                    const correctAnswer = question.correct_option;
                    
                    // Get full options text
                    const options = {
                      a: question.option_a,
                      b: question.option_b,
                      c: question.option_c,
                      d: question.option_d,
                    };
                    
                    return (
                      <Card key={question.id} className={`question-breakdown-card mb-3 ${isCorrect ? 'border-success' : 'border-danger'}`}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="mb-0">Question {idx + 1}</h6>
                            <Badge bg={isCorrect ? 'success' : 'danger'}>
                              {isCorrect ? '✓ Correct' : '✗ Incorrect'} ({answer?.marks || 0} / {question.marks} marks)
                            </Badge>
                          </div>
                          
                          <div 
                            className="question-text mb-3 p-2 bg-light rounded"
                            dangerouslySetInnerHTML={{ __html: question.question }}
                          />
                          
                          <div className="options-grid">
                            {['a', 'b', 'c', 'd'].map(opt => {
                              const isCorrectOption = correctAnswer === opt;
                              const isUserAnswer = userAnswer === opt;
                              
                              return (
                                <div 
                                  key={opt}
                                  className={`option-result-card 
                                    ${isCorrectOption ? 'correct-option' : ''} 
                                    ${isUserAnswer && !isCorrect ? 'wrong-option' : ''}
                                    ${isUserAnswer && isCorrect ? 'user-correct' : ''}`}
                                >
                                  <div className="option-letter">{opt.toUpperCase()}</div>
                                  <div 
                                    className="option-text"
                                    dangerouslySetInnerHTML={{ __html: options[opt] || '' }}
                                  />
                                  <div className="option-badges">
                                    {isCorrectOption && (
                                      <span className="badge correct-badge">
                                        <FaCheckCircle /> Correct Answer
                                      </span>
                                    )}
                                    {isUserAnswer && isCorrect && (
                                      <span className="badge user-correct-badge ms-1">
                                        <FaCheckCircle /> Your Answer ✓
                                      </span>
                                    )}
                                    {isUserAnswer && !isCorrect && (
                                      <span className="badge wrong-badge ms-1">
                                        <FaTimesCircle /> Your Answer ✗
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {!isCorrect && (
                            <Alert variant="info" className="mt-3 mb-0 py-2">
                              <strong>💡 Explanation:</strong> The correct answer is <strong>{correctAnswer.toUpperCase()}</strong>.
                            </Alert>
                          )}
                        </Card.Body>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {!showDetails && (
                <div className="text-center text-muted py-3">
                  <FaEye size={30} className="mb-2 opacity-50" />
                  <p>Click "Show Details" to see full question-wise breakdown with options</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Action Buttons */}
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-primary" onClick={() => navigate('/student/tests')}>
              <FaHome className="me-2" /> Back to Tests
            </Button>
            {!passed && (
              <Button variant="primary" onClick={() => navigate(`/student/take-test/${attemptId}`)}>
                <FaChartLine className="me-2" /> Retake Test
              </Button>
            )}
          </div>
        </Container>
      </div>
    </div>
  );
};

export default TestResult;